import PropTypes from 'prop-types';
// Components
import { Button } from '@repo/zolintra-ui';
// Hooks
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import useToast from '../../../hooks/useToast';
import { useDialog } from '../../../hooks/useDialog';
import usePost, { POST_STATUS } from '../../../hooks/domain/usePost';
import { useAppContext } from '../../../store/AppContext';
// Permissions
import hasPermission, { PERMISSIONS } from '../../../permissions/hasPermission';
// Services
import { deletePost, postAcceptPost, postRefusePost } from '../../../services/post/postService';
// Dialog
import DeleteConfirmDialog from '../dialogTemplates/DeleteConfirmDialog';

import styles from './Form.module.css';
import { updateLeavePost } from '../../../services/leavePost/leavePostService';

function ReadFormActions({
  postData, postDetail, year, isPostsListLoading,
}) {
  // Hooks
  const { t } = useTranslation();
  const [{ user }] = useAppContext();
  const toast = useToast();
  const { setDialog } = useDialog();
  const { hasDeletePostPermission, getDateFromSlotsId } = usePost();

  // Constants
  const startSlotId = Number(postData.includedStartSlotId);
  const startDate = getDateFromSlotsId({ slotId: startSlotId, year });

  // Permissions
  const hasAcceptPostPermission = hasPermission(PERMISSIONS.ACCEPT_POST, user.prm);
  const hasRefusePostPermission = hasPermission(PERMISSIONS.REFUSE_POST, user.prm);

  const canAcceptPost = hasAcceptPostPermission && (
    postData.status === POST_STATUS.PENDING
    || postData.status === POST_STATUS.REFUSED
  );
  const canRefusePost = hasRefusePostPermission && postData.status === POST_STATUS.PENDING;
  const canUpdatePost = hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)
  && postData.status === POST_STATUS.ACCEPTED;

  // Api calls
  const queryClient = useQueryClient();

  const handleApiCallSuccess = (message) => {
    queryClient.invalidateQueries({ queryKey: ['postsList'] });
    setDialog({ type: 'HIDE_DIALOG' });
    toast.notify({
      type: 'success',
      message: t(message),
    });
  };

  const deletePostMutation = useMutation({
    mutationFn: () => deletePost(
      {
        year: startDate.getFullYear().toString(),
        postId: postData.id,
      },
    ),
    onSuccess: () => {
      handleApiCallSuccess('remoteWorkingAndLeave.actions.deleteSuccess');
    },
  });
  const updatePostMutation = useMutation({
    mutationFn: () => updateLeavePost(
      {
        year: startDate.getFullYear().toString(),
        postId: postData.id,
      },
      postDetail,
    ),
    onSuccess: () => {
      handleApiCallSuccess('remoteWorkingAndLeave.actions.updateSuccess');
      queryClient.invalidateQueries({
        queryKey: ['remoteWorkingPost', {
          year: startDate.getFullYear().toString(),
          postId: postData.id,
        }],
      });
    },
    meta: {
      preventToastError: postData.status === POST_STATUS.PENDING,
    },
  });
  const acceptPostMutation = useMutation({
    mutationFn: postAcceptPost,
    onSuccess: () => {
      if (postDetail?.type) {
        updateLeavePost({
          year: startDate.getFullYear().toString(),
          postId: postData.id,
        }, postDetail).then(() => {
          handleApiCallSuccess('remoteWorkingAndLeave.actions.acceptSuccess');
        });
      } else {
        handleApiCallSuccess('remoteWorkingAndLeave.actions.acceptSuccess');
      }
    },
  });
  const refusePostMutation = useMutation({
    mutationFn: postRefusePost,
    onSuccess: () => {
      handleApiCallSuccess('remoteWorkingAndLeave.actions.refuseSuccess');
    },
  });

  const disabledButtonOnPending = useMemo(() => (
    isPostsListLoading
      || deletePostMutation.isPending
      || acceptPostMutation.isPending
      || refusePostMutation.isPending
      || updatePostMutation.isPending
  ), [
    isPostsListLoading,
    deletePostMutation.isPending,
    acceptPostMutation.isPending,
    refusePostMutation.isPending,
    updatePostMutation.isPending,
  ]);

  return (
    <div className={styles['submit-buttons']}>
      {hasDeletePostPermission({ year, post: postData }) ? (
        <Button
          onClick={() => setDialog({
            type: 'SHOW_DIALOG',
            payload: {
              title: t('remoteWorkingAndLeave.deleteDialog.title'),
              children: (
                <DeleteConfirmDialog
                  request={{
                    resourceId: postData.resourceId,
                    includedStartSlotId: postData.includedStartSlotId,
                    id: postData.id,
                  }}
                  year={year}
                />
              ),
              onClose: () => setDialog({ type: 'HIDE_DIALOG' }),
            },
          })}
          color="alert500"
          disabled={disabledButtonOnPending}
          isLoading={deletePostMutation.isPending}
          className={canAcceptPost && canRefusePost ? styles['action-button-left'] : ''}
        >
          {t('remoteWorkingAndLeave.actions.delete')}
        </Button>
      ) : null}

      {canRefusePost ? (
        <Button
          onClick={() => refusePostMutation.mutate({ year, postId: postData.id })}
          variant="bordered"
          color="alert500"
          disabled={disabledButtonOnPending}
          isLoading={refusePostMutation.isPending}
        >
          {t('remoteWorkingAndLeave.actions.refuse')}
        </Button>
      ) : null}

      {canAcceptPost ? (
        <Button
          onClick={() => acceptPostMutation.mutate({ year, postId: postData.id })}
          disabled={disabledButtonOnPending}
          isLoading={acceptPostMutation.isPending}
        >
          {t('remoteWorkingAndLeave.actions.accept')}
        </Button>
      ) : null}

      {canUpdatePost && !!postDetail?.version ? (
        <Button
          onClick={() => updatePostMutation.mutate()}
          disabled={disabledButtonOnPending || !postDetail?.type}
          isLoading={updatePostMutation.isPending}
        >
          {t('remoteWorkingAndLeave.actions.update')}
        </Button>
      ) : null}

    </div>
  );
}

ReadFormActions.propTypes = {
  postData: PropTypes.shape({
    id: PropTypes.string,
    includedStartSlotId: PropTypes.number,
    status: PropTypes.string,
  }).isRequired,
  postDetail: {
    adress: PropTypes.string,
    comment: PropTypes.string,
    type: PropTypes.string,
    version: PropTypes.number,
  },
  year: PropTypes.string.isRequired,
  isPostsListLoading: PropTypes.bool,
};

ReadFormActions.defaultProps = {
  isPostsListLoading: false,
  postDetail: {},
};

export default ReadFormActions;
